<template>
  <v-menu
    offset-y
    :close-on-content-click="false"
    nudge-bottom="10"
    max-width="400"
  >
    <template #activator="{ on: menu, attrs }">
      <v-tooltip bottom>
        <template #activator="{ on: tooltip }">
          <v-btn
            icon
            small
            class="mr-2"
            v-bind="attrs"
            data-test="notification-bell-btn"
            v-on="{ ...tooltip, ...menu }"
          >
            <div class="bell">
              <PhBell :size="20" />
              <span
                v-if="unreadCount > 0"
                class="bell__badge"
                data-test="notification-badge"
              >
                {{ formattedBadgeCount }}
              </span>
            </div>
          </v-btn>
        </template>
        <span>Notificações</span>
      </v-tooltip>
    </template>

    <div
      class="np"
      data-test="notification-panel"
    >
      <header class="np__head">
        <h2 class="np__title">Notificações</h2>
        <span
          v-if="unreadCount > 0"
          class="np__count"
        >{{ formattedBadgeCount }}</span>

        <v-spacer />

        <button
          v-if="!isSelectionMode && hasNotifications"
          type="button"
          class="np__link"
          data-test="select-mode-btn"
          @click.stop="toggleSelectionMode"
        >
          Selecionar
        </button>
        <button
          v-if="isSelectionMode"
          type="button"
          class="np__link"
          @click.stop="cancelSelection"
        >
          Cancelar
        </button>
      </header>

      <div
        v-if="isSelectionMode"
        class="np__selection"
      >
        <v-checkbox
          :input-value="isAllSelected"
          :indeterminate="isPartiallySelected"
          dense
          hide-details
          class="np__check"
          @change="toggleSelectAll"
        />
        <span class="np__selection-count">
          {{ selectedCount }} selecionada{{ selectedCount !== 1 ? 's' : '' }}
        </span>
        <v-spacer />

        <!-- Ação em ícone, não em texto: são três ações e o painel tem 400px —
             com rótulo escrito a barra quebrava em três linhas. -->
        <!-- O v-if vive no tooltip, não no botão: o slot `activator` precisa
             entregar sempre um elemento, e condicionar por dentro deixava o
             Vuetify sem âncora — o botão não chegava a renderizar. -->
        <v-tooltip
          v-if="hasSelectedUnread"
          bottom
        >
          <template #activator="{ on }">
            <button
              type="button"
              class="np__icon-action"
              data-test="mark-selected-read"
              v-on="on"
              @click.stop="markSelectedAsRead"
            >
              <PhEnvelopeOpen :size="16" />
            </button>
          </template>
          <span>Marcar como lida</span>
        </v-tooltip>

        <v-tooltip
          v-if="hasSelectedRead"
          bottom
        >
          <template #activator="{ on }">
            <button
              type="button"
              class="np__icon-action"
              data-test="mark-selected-unread"
              v-on="on"
              @click.stop="markSelectedAsUnread"
            >
              <PhEnvelope :size="16" />
            </button>
          </template>
          <span>Marcar como não lida</span>
        </v-tooltip>

        <v-tooltip bottom>
          <template #activator="{ on }">
            <button
              type="button"
              class="np__icon-action is-danger"
              :disabled="selectedCount === 0"
              data-test="delete-selected"
              v-on="on"
              @click.stop="deleteSelected"
            >
              <PhTrash :size="16" />
            </button>
          </template>
          <span>Excluir</span>
        </v-tooltip>
      </div>

      <div class="np__body">
        <div
          v-if="isLoadingNotifications"
          class="np__loading"
        >
          <v-progress-circular
            indeterminate
            size="22"
            width="2"
            color="primary"
          />
        </div>

        <div
          v-else-if="!hasNotifications"
          class="np__empty"
        >
          <PhBellSlash :size="28" />
          <span class="np__empty-title">Tudo em dia</span>
          <span class="np__empty-sub">Você não tem notificações</span>
        </div>

        <template v-else>
          <template v-if="hasUnreadNotifications">
            <p class="np__section tc-kicker">Novas</p>
            <ul class="np__list tc-hairline-list">
              <NotificationRow
                v-for="item in unreadNotifications"
                :key="'u-' + item.id"
                :item="item"
                unread
                :selectable="isSelectionMode"
                :selected="isSelected(item.id)"
                data-test="notification-card-unread"
                @select="toggleSelection"
                @open="onRowClick"
              />
            </ul>
          </template>

          <template v-if="hasReadNotifications">
            <p class="np__section tc-kicker">Anteriores</p>
            <ul class="np__list tc-hairline-list">
              <NotificationRow
                v-for="item in readNotifications"
                :key="'r-' + item.id"
                :item="item"
                :selectable="isSelectionMode"
                :selected="isSelected(item.id)"
                data-test="notification-card-read"
                @select="toggleSelection"
                @open="onRowClick"
              />
            </ul>
          </template>

          <div
            v-if="hasMore"
            class="np__more"
          >
            <button
              type="button"
              class="np__link"
              :disabled="isLoadingMore"
              data-test="load-more-btn"
              @click.stop="loadMore"
            >
              {{ isLoadingMore ? 'Carregando…' : 'Carregar mais' }}
            </button>
          </div>
        </template>
      </div>

      <footer class="np__foot">
        <button
          type="button"
          class="np__link"
          :disabled="unreadCount === 0"
          @click.stop="markAllNotificationsAsRead"
        >
          Marcar todas como lidas
        </button>
        <v-spacer />
        <button
          type="button"
          class="np__link is-action"
          data-test="history-link"
          @click.stop="goToHistory"
        >
          Ver histórico
          <PhArrowRight :size="13" />
        </button>
      </footer>
    </div>
  </v-menu>
</template>

<script>
import NotificationApi from '@/api/notification/notification-api';
import notificationPresentation from '@/mixin/mixin-notification-presentation';
import notificationNavigation from '@/mixin/mixin-notification-navigation';
import NotificationRow from '@/components/menu/NotificationRow';

const PAGE_SIZE = 20;

export default {
  name: 'NotificationPanel',
  components: { NotificationRow },
  mixins: [notificationPresentation, notificationNavigation],
  inject: ['appData'],
  data() {
    return {
      notificationApi: new NotificationApi(this.appData.currentUser),
      allNotifications: [],
      unreadNotifications: [],
      readNotifications: [],
      unreadCount: 0,
      page: 1,
      hasMore: false,
      isLoadingNotifications: false,
      isLoadingMore: false,
      isSelectionMode: false,
      selectedIds: []
    };
  },
  computed: {
    formattedBadgeCount() {
      return this.unreadCount > 99 ? '99+' : this.unreadCount;
    },

    hasUnreadNotifications() {
      return this.unreadNotifications.length > 0;
    },

    hasReadNotifications() {
      return this.readNotifications.length > 0;
    },

    hasNotifications() {
      return this.allNotifications.length > 0;
    },

    selectedCount() {
      return this.selectedIds.length;
    },

    isAllSelected() {
      return this.selectedCount > 0 && this.selectedCount === this.allNotifications.length;
    },

    isPartiallySelected() {
      return this.selectedCount > 0 && !this.isAllSelected;
    },

    hasSelectedUnread() {
      return this.selectedIds.some((id) => this._isSelectedWithRead(id, false));
    },

    hasSelectedRead() {
      return this.selectedIds.some((id) => this._isSelectedWithRead(id, true));
    }
  },
  watch: {
    unreadCount(newVal) {
      this.$emit('notification-count-changed', newVal);
    }
  },
  mounted() {
    this.loadNotificationsFromApi();
    this.$eventBus.$on('notification-updated', this.loadNotificationsFromApi);
  },
  beforeDestroy() {
    this.$eventBus.$off('notification-updated', this.loadNotificationsFromApi);
  },
  methods: {
    goToHistory() {
      this.$router.push({ name: 'NotificationHistory' }).catch(() => {});
    },

    onRowClick(item) {
      if (this.isSelectionMode) return this.toggleSelection(item.id);

      if (!item.read) this.markNotificationAsRead(item.id);

      return this.navigateToNotificationTarget(item);
    },

    toggleSelectionMode() {
      this.isSelectionMode = true;
      this.selectedIds = [];
    },

    cancelSelection() {
      this.isSelectionMode = false;
      this.selectedIds = [];
    },

    isSelected(id) {
      return this.selectedIds.includes(id);
    },

    toggleSelection(id) {
      const index = this.selectedIds.indexOf(id);

      if (index === -1) {
        this.selectedIds.push(id);
      } else {
        this.selectedIds.splice(index, 1);
      }
    },

    toggleSelectAll(checked) {
      this.selectedIds = checked ? this.allNotifications.map((n) => n.id) : [];
    },

    _isSelectedWithRead(id, read) {
      const notif = this.allNotifications.find((n) => n.id === id);

      return !!notif && notif.read === read;
    },

    markSelectedAsRead() {
      /* Só o que ainda não está no estado alvo: reenviar o resto seria uma
         chamada por item para não mudar nada. */
      return this._applyReadToSelection(true);
    },

    markSelectedAsUnread() {
      return this._applyReadToSelection(false);
    },

    async _applyReadToSelection(read) {
      try {
        const ids = this.selectedIds.filter((id) => this._isSelectedWithRead(id, !read));

        for (const id of ids) {
          await (read ? this.notificationApi.markAsRead(id) : this.notificationApi.markAsUnread(id));
        }

        this.cancelSelection();
        this.loadNotificationsFromApi();
        this.$eventBus.$emit('notification-updated');
      } catch (error) {
        // eslint-disable-next-line no-empty
      }
    },

    async deleteSelected() {
      try {
        await this.notificationApi.deleteByIds(this.selectedIds);
        this.cancelSelection();
        this.loadNotificationsFromApi();
        this.$eventBus.$emit('notification-updated');
      } catch (error) {
        // eslint-disable-next-line no-empty
      }
    },

    async loadNotificationsFromApi() {
      try {
        /* Só mostra spinner no primeiro carregamento — em recarga por leitura
           ou exclusão a lista já está na tela e piscar dá sensação de erro. */
        this.isLoadingNotifications = !this.allNotifications.length;

        /* Recarrega tudo que já estava visível: sem isso, marcar uma como
           lida encolhia a lista de volta para a primeira página. */
        const response = await this.notificationApi.findAll({
          page: 1,
          limit: PAGE_SIZE * this.page
        });

        this._applyResponse(response, false);
        this.migrateLocalStorageNotifications();
      } catch (error) {
        /* Mantém o que já está na tela: o fallback antigo lia uma chave do
           localStorage que a própria migração apaga, então mostrava vazio. */
      } finally {
        this.isLoadingNotifications = false;
      }
    },

    async loadMore() {
      try {
        this.isLoadingMore = true;

        const nextPage = this.page + 1;
        const response = await this.notificationApi.findAll({ page: nextPage, limit: PAGE_SIZE });

        this._applyResponse(response, true);
        this.page = nextPage;
      } catch (error) {
        // eslint-disable-next-line no-empty
      } finally {
        this.isLoadingMore = false;
      }
    },

    _applyResponse(response, append) {
      const data = response?.data?.data || [];
      const meta = response?.data?.meta || {};

      this.allNotifications = append ? [...this.allNotifications, ...data] : data;
      this.unreadNotifications = this.allNotifications.filter((n) => !n.read);
      this.readNotifications = this.allNotifications.filter((n) => n.read);

      /* Contagem vem do backend: contar só o que está carregado subestimava
         o badge assim que a lista passava de uma página. */
      this.unreadCount = Number.isFinite(meta.unreadCount)
        ? meta.unreadCount
        : this.unreadNotifications.length;

      this.hasMore = !!meta.hasMore;
    },

    async migrateLocalStorageNotifications() {
      try {
        if (sessionStorage.getItem('notificationMigrationAttempted')) return;

        const stored = localStorage.getItem('notifications-tc');

        if (!stored) return;

        const allData = JSON.parse(stored);
        const notifications = allData?.notifications || [];

        if (!notifications.length) return;

        sessionStorage.setItem('notificationMigrationAttempted', 'true');
        await this.notificationApi.migrate(notifications);
        localStorage.removeItem('notifications-tc');
        this.loadNotificationsFromApi();
      } catch (error) {
        // eslint-disable-next-line no-empty
      }
    },

    async markNotificationAsRead(notificationId) {
      try {
        await this.notificationApi.markAsRead(notificationId);
        this.unreadCount = Math.max(0, this.unreadCount - 1);
        this.loadNotificationsFromApi();
        this.$eventBus.$emit('notification-updated');
      } catch (error) {
        // eslint-disable-next-line no-empty
      }
    },

    async markAllNotificationsAsRead() {
      try {
        await this.notificationApi.markAllAsRead();
        this.unreadCount = 0;
        this.loadNotificationsFromApi();
        this.$eventBus.$emit('notification-updated');
      } catch (error) {
        // eslint-disable-next-line no-empty
      }
    }
  }
};
</script>

<style scoped>
/* Sino ------------------------------------------------------------------- */
.bell {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

/* Raio-pílula é sancionado pelo DS para badge numérico — e só para ele. */
.bell__badge {
  position: absolute;
  top: -8px;
  right: -9px;
  min-width: 17px;
  height: 17px;
  padding: 0 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--tc-radius-pill);
  border: 2px solid var(--tc-bg);
  background: var(--tc-danger);
  color: #fff;
  font-family: var(--tc-font-mono);
  font-size: 10px;
  font-weight: var(--tc-fw-medium);
  font-variant-numeric: tabular-nums;
  line-height: 1;
}

/* Painel ----------------------------------------------------------------- */
/* Sem fundo, borda ou raio próprios: quem emoldura o overlay é o
   .v-menu__content (regra §10 do design system). Pintar aqui também produzia
   duas superfícies empilhadas com raios diferentes. */
.np {
  width: 400px;
  max-width: 100%;
  display: flex;
  flex-direction: column;
  max-height: 560px;
  overflow: hidden;
}

.np__head,
.np__foot {
  display: flex;
  align-items: center;
  gap: var(--tc-space-2);
  flex: 0 0 auto;
}

.np__head {
  padding: var(--tc-space-4) var(--tc-space-4) var(--tc-space-3);
}

.np__title {
  font-family: var(--tc-font-display);
  font-size: var(--tc-fs-h4);
  font-weight: var(--tc-fw-semibold);
  letter-spacing: var(--tc-tracking-card);
  line-height: var(--tc-lh-tight);
  margin: 0;
  color: var(--tc-text);
}

.np__count {
  min-width: 20px;
  padding: 1px 6px;
  border-radius: var(--tc-radius-pill);
  background: var(--tc-action-tint-strong);
  color: var(--tc-action);
  font-family: var(--tc-font-mono);
  font-size: 11px;
  font-weight: var(--tc-fw-medium);
  font-variant-numeric: tabular-nums;
  text-align: center;
}

/* Ações do painel são texto, não v-btn: dentro de um overlay de 400px um
   botão de 36px cria hierarquia que compete com as próprias notificações. */
.np__link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 6px;
  border-radius: var(--tc-radius-sm);
  background: none;
  border: 0;
  font-family: var(--tc-font-body);
  font-size: var(--tc-fs-sm);
  font-weight: var(--tc-fw-medium);
  color: var(--tc-text-2);
  cursor: pointer;
  transition: color var(--tc-transition), background-color var(--tc-transition);
}

.np__link:hover:not(:disabled) {
  color: var(--tc-action);
  background: var(--tc-action-tint);
}

.np__link:disabled {
  color: var(--tc-text-3);
  cursor: default;
}

.np__link.is-action {
  color: var(--tc-action);
}

.np__link.is-danger:hover:not(:disabled) {
  color: var(--tc-danger);
  background: var(--tc-danger-soft);
}

.np__icon-action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  flex: 0 0 auto;
  border: 0;
  border-radius: var(--tc-radius-sm);
  background: none;
  color: var(--tc-text-2);
  cursor: pointer;
  transition: color var(--tc-transition), background-color var(--tc-transition);
}

.np__icon-action:hover:not(:disabled) {
  color: var(--tc-action);
  background: var(--tc-action-tint);
}

.np__icon-action:disabled {
  color: var(--tc-text-3);
  cursor: default;
}

.np__icon-action.is-danger:hover:not(:disabled) {
  color: var(--tc-danger);
  background: var(--tc-danger-soft);
}

/* Barra de seleção ------------------------------------------------------- */
.np__selection {
  display: flex;
  align-items: center;
  gap: var(--tc-space-2);
  padding: var(--tc-space-2) var(--tc-space-4);
  background: var(--tc-surface-2);
  border-top: 1px solid var(--tc-line);
  border-bottom: 1px solid var(--tc-line);
  flex: 0 0 auto;
}

.np__check {
  margin: 0;
  padding: 0;
  flex: 0 0 auto;
}

.np__selection-count {
  font-size: var(--tc-fs-sm);
  font-weight: var(--tc-fw-medium);
  color: var(--tc-text-2);
}

/* Corpo ------------------------------------------------------------------ */
.np__body {
  flex: 1 1 auto;
  overflow-y: auto;
  padding: 0 var(--tc-space-4) var(--tc-space-2);
}

.np__body::-webkit-scrollbar {
  width: 4px;
}

.np__body::-webkit-scrollbar-thumb {
  background: var(--tc-line);
  border-radius: var(--tc-radius-pill);
}

.np__section {
  margin: var(--tc-space-3) 0 var(--tc-space-1);
  font-size: 10px;
}

.np__list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.np__more {
  display: flex;
  justify-content: center;
  padding-top: var(--tc-space-2);
  border-top: 1px solid var(--tc-line);
}

.np__loading {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--tc-space-8) 0;
}

.np__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--tc-space-1);
  padding: var(--tc-space-8) var(--tc-space-4);
  color: var(--tc-text-3);
}

.np__empty-title {
  margin-top: var(--tc-space-2);
  font-size: var(--tc-fs-body);
  font-weight: var(--tc-fw-semibold);
  color: var(--tc-text-2);
}

.np__empty-sub {
  font-size: var(--tc-fs-sm);
  color: var(--tc-text-3);
}

/* Rodapé ----------------------------------------------------------------- */
.np__foot {
  padding: var(--tc-space-2) var(--tc-space-3);
  border-top: 1px solid var(--tc-line);
}
</style>

